import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
  LeadAttribution,
} from '../../services/facebookAdsService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const FacebookAdsLeads: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<FacebookConnection | null>(null);
  const [leads, setLeads] = useState<LeadAttribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection) {
      loadLeads();
    }
  }, [selectedConnection]);

  const loadConnections = async () => {
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
        if (response.data.length > 0 && !selectedConnection) {
          setSelectedConnection(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    if (!selectedConnection) return;

    setLoading(true);
    try {
      const response = await FacebookAdsService.getLeads(selectedConnection.account_id.toString(), { limit: 100 });
      if (response.success && response.data) {
        setLeads(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (loading && leads.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Leads Atribuídos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Conta</InputLabel>
            <Select
              value={selectedConnection?.account_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                const conn = connections.find((c) => c.account_id === parseInt(value.toString()));
                setSelectedConnection(conn || null);
              }}
              label="Conta"
            >
              {connections.map((conn) => (
                <MenuItem key={conn.account_id} value={conn.account_id}>
                  {conn.account_name || `Conta ${conn.account_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadLeads} disabled={loading}>
            Atualizar
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lead</TableCell>
              <TableCell>Campanha</TableCell>
              <TableCell>AdSet</TableCell>
              <TableCell>Anúncio</TableCell>
              <TableCell align="right">Data</TableCell>
              <TableCell align="right">Gasto no Dia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.map((lead) => (
              <TableRow key={lead.contact_id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {lead.lead_name || 'Sem nome'}
                  </Typography>
                  {lead.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lead.email}
                    </Typography>
                  )}
                  {lead.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lead.phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{lead.campaign_name || 'N/A'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{lead.adset_name || 'N/A'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{lead.ad_name || 'N/A'}</Typography>
                </TableCell>
                <TableCell align="right">
                  {format(new Date(lead.lead_created_at), 'dd/MM/yyyy', { locale: ptBR })}
                </TableCell>
                <TableCell align="right">
                  {lead.ad_spend_on_lead_date
                    ? formatCurrency(lead.ad_spend_on_lead_date)
                    : 'N/A'}
                </TableCell>
              </TableRow>
            ))}
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">Nenhum lead atribuído encontrado</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FacebookAdsLeads;


